import { Link } from 'react-router'
import { useOptionalAdminUser } from '#app/utils/user'
import documents from '#content/documents.ts'
import minutes from '#content/minutes.ts'
import { Icon, ToolTipIcon } from './ui/icon'
import {
	NavigationMenu,
	NavigationMenuContent,
	NavigationMenuItem,
	NavigationMenuLink,
	NavigationMenuList,
	NavigationMenuTrigger,
	NavigationSubMenuItem,
	NavigationSubMenuItemHorizontal,
	navigationMenuTriggerStyle,
} from './ui/navigation-menu'

const iconStyle = 'mr-1 mt-0.5 w-5 h-5 md:max-2xl:hidden max-md:w-6 max-md:h-6'

export function MainNavigationMenu({
	schedules,
}: {
	schedules: {
		signup: { date: string; deadline: string; state: string } | null | undefined
		current: { date: string } | null | undefined
		previous: { date: string } | null | undefined
	}
}) {
	const userIsAdmin = useOptionalAdminUser() ?? false

	function IrrigationNavigationMenu() {
		return (
			<NavigationMenu>
				<NavigationMenuList>
					<NavigationMenuItem>
						<NavigationMenuTrigger>
							<Icon className={iconStyle} name="droplets" aria-hidden="true" />
							<p className="max-md:hidden">Irrigation</p>
						</NavigationMenuTrigger>
						<NavigationMenuContent className="hover:z-10">
							<div className="flex w-[300px] flex-col flex-wrap gap-3 p-4 lg:w-[500px]">
								<div id="irrigation-schedules-header" className="w-full">
									<NavigationMenuLink asChild>
										<Link to="/schedules">
											<div className="z-1 flex h-full w-full flex-grow select-none flex-col items-center justify-center rounded-md bg-gradient-to-b from-muted/50 to-muted p-6 no-underline outline-none focus:shadow-md">
												<div className="flex flex-row">
													<Icon
														name="droplet"
														className="h-8 w-8"
														aria-hidden="true"
													/>
													<Icon
														name="droplets"
														className="h-8 w-8"
														aria-hidden="true"
													/>
												</div>
												<div className="mb-2 mt-2 text-lg font-medium">
													Irrigation Schedules
												</div>
											</div>
										</Link>
									</NavigationMenuLink>
								</div>
								<div
									id="irrigation-schedules"
									className="flex flex-row flex-wrap gap-2"
								>
									{[schedules?.previous, schedules?.current].map(
										(schedule, index) => (
											<div
												key={`closed-${schedule?.date}`}
												className={`flex w-[265px] flex-col rounded-md border-2 border-solid lg:w-[150px] ${index ? 'border-green-700' : 'border-red-700'}`}
											>
												<div className="text-md mb-2 w-full rounded-t-sm border-b-2 border-solid bg-secondary p-2 text-center font-bold">
													{schedule?.date}
												</div>
												<Link
													to={`/schedule/${schedule?.date}/timeline`}
													className="border-1 m-1.5 rounded-md border-solid"
												>
													<NavigationSubMenuItem key="schedule" title="">
														<div className="text-md my-0.5 font-medium">
															{index ? 'Current Timeline' : 'Previous Timeline'}
														</div>
													</NavigationSubMenuItem>
												</Link>
												<Link
													to={`documents/schedules/${schedule?.date}`}
													className="border-1 m-1.5 rounded-md border-solid"
												>
													<NavigationSubMenuItem key="schedule-notes">
														<div className="text-md my-0.5 font-medium">
															Schedule Notes
														</div>
													</NavigationSubMenuItem>
												</Link>
												<Link
													to={`schedule/${schedule?.date}/crossovers`}
													className="border-1 m-1.5 rounded-md border-solid bg-yellow-700 "
												>
													<NavigationSubMenuItem key="acknowledgements-volunteers">
														<div className="text-md my-0.5 font-medium text-white hover:text-yellow-700">
															Gate Changes & Crossovers
														</div>
													</NavigationSubMenuItem>
												</Link>
											</div>
										),
									)}
									<div className="flex w-[265px] flex-col rounded-md border-2 border-solid border-blue-700 lg:w-[150px]">
										<div className="text-md mb-2 w-full rounded-t-sm border-b-2 border-solid bg-secondary p-2 text-center align-middle font-bold">
											{schedules?.signup ? `${schedules?.signup.date}` : ''}
										</div>
										<div className="flex flex-col">
											<div className="text-md my-0.5 line-clamp-2 block h-full select-none space-y-1 rounded-md px-3 py-4 font-medium text-muted-foreground no-underline outline-none transition-colors">
												{schedules?.signup?.state === 'locked' ? (
													<p className="text-destructive">
														This schedule is locked.
													</p>
												) : (
													<>
														Deadline
														<br />
														{schedules?.signup?.deadline}
													</>
												)}
											</div>
											<Link
												to={`/schedule/${schedules?.signup?.date}/signup`}
												className={`${schedules?.signup ? '' : 'pointer-events-none'} border-1 m-1.5 rounded-md border-solid`}
											>
												<NavigationSubMenuItem key="signup" title="">
													<div className="text-md my-0.5 font-medium">
														{schedules?.signup
															? 'View Sign Up Sheet'
															: 'No Schedules Open'}
													</div>
												</NavigationSubMenuItem>
											</Link>
											<Link
												to="/irrigation"
												className="border-1 m-1.5 rounded-md border-solid bg-sky-800 text-white hover:text-sky-800"
											>
												<NavigationSubMenuItem key="water-location" title="">
													<div className="text-md my-0.5 font-medium text-white hover:text-sky-800">
														Where is the water?
													</div>
												</NavigationSubMenuItem>
											</Link>
										</div>
									</div>
								</div>
							</div>
						</NavigationMenuContent>
					</NavigationMenuItem>
				</NavigationMenuList>
			</NavigationMenu>
		)
	}

	function DocumentsNavigationMenu() {
		return (
			<NavigationMenu>
				<NavigationMenuList>
					<NavigationMenuItem>
						<NavigationMenuTrigger>
							<Icon className={iconStyle} name="file-text" aria-hidden="true" />
							<p className="max-md:hidden">Documents</p>
						</NavigationMenuTrigger>
						<NavigationMenuContent>
							<ol className="grid max-h-[50vh] w-[300px] grid-cols-1 gap-3 overflow-y-scroll p-4 md:w-[400px] lg:w-[500px] lg:grid-cols-2">
								<li className="row-span-2">
									<NavigationMenuLink asChild>
										{userIsAdmin ? (
											<Link to="/keystatic">
												<DocumentsAdminPanel />
											</Link>
										) : (
											<DocumentsAdminPanel />
										)}
									</NavigationMenuLink>
								</li>
								{documents.map((doc) => (
									<NavigationSubMenuItem key={doc.title} {...doc}>
										{doc.description}
									</NavigationSubMenuItem>
								))}
							</ol>
						</NavigationMenuContent>
					</NavigationMenuItem>
				</NavigationMenuList>
			</NavigationMenu>
		)
	}

	function DocumentsAdminPanel() {
		return (
			<div className="z-1 flex h-full w-full flex-grow select-none flex-col items-center justify-center rounded-md bg-gradient-to-b from-muted/50 to-muted p-6 no-underline outline-none focus:shadow-md">
				<div className="flex flex-row">
					<Icon name="cwf" className="h-24 w-24" aria-hidden="true" />
				</div>
				<div className="mb-2 mt-2 text-xl font-medium">Documents</div>
				{userIsAdmin ? (
					<p className="text-sm leading-tight text-muted-foreground">
						Admin Panel
					</p>
				) : (
					<></>
				)}
			</div>
		)
	}

	function MeetingsNavigationMenu() {
		return (
			<NavigationMenu>
				<NavigationMenuList>
					<NavigationMenuItem>
						<NavigationMenuTrigger>
							<Icon className={iconStyle} name="calendar" aria-hidden="true" />
							<p className="max-md:hidden">Meetings</p>
						</NavigationMenuTrigger>
						<NavigationMenuContent>
							<ol className="grid max-h-[50vh] w-[320px] grid-cols-1 gap-3 overflow-y-scroll p-4">
								<li className="row-span-3">
									<NavigationMenuLink asChild>
										{userIsAdmin ? (
											<Link to="/documents">
												<MeetingsAdminPanel />
											</Link>
										) : (
											<MeetingsAdminPanel />
										)}
									</NavigationMenuLink>
								</li>
								{minutes
									.filter(({ executive }) => !executive || userIsAdmin)
									.map((doc) => (
										<NavigationSubMenuItemHorizontal key={doc.title} {...doc}>
											{doc.description}
										</NavigationSubMenuItemHorizontal>
									))}
							</ol>
						</NavigationMenuContent>
					</NavigationMenuItem>
				</NavigationMenuList>
			</NavigationMenu>
		)
	}

	function MeetingsAdminPanel() {
		return (
			<div className="z-1 flex h-full w-full flex-grow select-none flex-col items-center justify-center rounded-md bg-gradient-to-b from-muted/50 to-muted p-6 no-underline outline-none focus:shadow-md">
				<div className="flex flex-row">
					<Icon name="calendar" className="h-14 w-14" aria-hidden="true" />
				</div>
				<div className="mb-2 mt-2 text-xl font-medium">Meeting Minutes</div>
				{userIsAdmin ? (
					<p className="text-sm leading-tight text-muted-foreground">
						Admin Panel
					</p>
				) : (
					<></>
				)}
			</div>
		)
	}

	function MembersNavigationMenu() {
		return (
			<NavigationMenu>
				<NavigationMenuList>
					<NavigationMenuItem>
						<NavigationMenuTrigger>
							<ToolTipIcon
								className={iconStyle}
								name="users"
								tooltip="Members"
								aria-hidden="true"
							/>
							<p className="max-md:hidden">Members</p>
						</NavigationMenuTrigger>
						<NavigationMenuContent className="hover:z-10">
							<ul className="grid w-[300px] grid-cols-1 gap-3 p-4 md:w-[400px] lg:w-[500px] lg:grid-cols-[.75fr_1fr]">
								<li className="row-span-3">
									<NavigationMenuLink asChild>
										<Link to="/members">
											<div className="z-1 flex h-full w-full select-none flex-col items-center justify-center rounded-md bg-gradient-to-b from-muted/50 to-muted p-6 no-underline outline-none focus:shadow-md">
												<div className="flex flex-row">
													<Icon
														name="user-search"
														className="h-8 w-8"
														aria-hidden="true"
													/>
												</div>
												<div className="mb-1 mt-2 text-lg font-medium">
													Members
												</div>
												<p className="text-sm leading-tight text-muted-foreground">
													Balances
												</p>
											</div>
										</Link>
									</NavigationMenuLink>
								</li>
								<Link to="/members/transactions">
									<NavigationSubMenuItem
										key="members-transactions"
										title="Transactions"
									>
										<div className="mb-1 mt-2 text-sm font-medium">
											Members' Transactions
										</div>
									</NavigationSubMenuItem>
								</Link>
								<Link to="/members/contacts">
									<NavigationSubMenuItem
										key="members-contacts"
										title="Contact List"
									>
										<div className="mb-1 mt-2 text-sm font-medium">
											Members' Contact Information
										</div>
									</NavigationSubMenuItem>
								</Link>
								<Link to="/members/restrictions">
									<NavigationSubMenuItem
										key="members-restrictions"
										title="Restrictions"
									>
										<div className="mb-1 mt-2 text-sm font-medium">
											Restriction Information
										</div>
									</NavigationSubMenuItem>
								</Link>
							</ul>
						</NavigationMenuContent>
					</NavigationMenuItem>
				</NavigationMenuList>
			</NavigationMenu>
		)
	}

	return (
		<NavigationMenu>
			<NavigationMenuList>
				<NavigationMenuItem>
					<Link to="/">
						<NavigationMenuLink className={navigationMenuTriggerStyle()}>
							<ToolTipIcon
								className={iconStyle}
								name="home"
								tooltip="Home"
								aria-hidden="true"
							/>
							<p className="max-md:hidden">Home</p>
						</NavigationMenuLink>
					</Link>
				</NavigationMenuItem>
				<IrrigationNavigationMenu />
				<DocumentsNavigationMenu />
				<MeetingsNavigationMenu />
				{userIsAdmin ? <MembersNavigationMenu /> : null}
				<NavigationMenuItem>
					<Link to="/trade-list">
						<NavigationMenuLink className={navigationMenuTriggerStyle()}>
							<ToolTipIcon
								className={iconStyle}
								name="id-card"
								tooltip="Trades & Resources"
								aria-hidden="true"
							/>
							<p className="text-ellipsis text-nowrap max-md:hidden">
								Trades & Resources
							</p>
						</NavigationMenuLink>
					</Link>
				</NavigationMenuItem>
				<NavigationMenuItem>
					<Link to="/contact-us">
						<NavigationMenuLink className={navigationMenuTriggerStyle()}>
							<ToolTipIcon
								className={iconStyle}
								name="envelope-closed"
								tooltip="Contact Us"
								aria-hidden="true"
							/>
							<p className="text-ellipsis text-nowrap max-md:hidden">
								Contact Us
							</p>
						</NavigationMenuLink>
					</Link>
				</NavigationMenuItem>
			</NavigationMenuList>
		</NavigationMenu>
	)
}
